import type { BreadcrumbItem } from '../components/breadcrumbs/breadcrumbs';

/**
 * Where "back" goes from a member's access editor.
 *
 * The editor is reached from four places — the Members list, the Access overview,
 * a customer's Team access tab, a customer's Members tab — and each stamps
 * `?returnTo=<its own URL>` when it links in. The editor's one ancestor is that
 * page, so back lands where you left, with the search and filters you had.
 *
 * `returnTo` is a query parameter, so it is never trusted: only these four
 * in-app destinations are recognised, only their own filter parameters are kept,
 * and anything else falls back to the Members list.
 */

interface Target {
  /** Matches the path (no query). Capture group 1 is a customer id, when there is one. */
  pattern: RegExp;
  label: string;
  /** The query parameters that page owns and that are worth coming back to. */
  keep: readonly string[];
  /** Router commands for the path. */
  commands: (customerId: string | undefined) => unknown[];
}

const TARGETS: readonly Target[] = [
  {
    pattern: /^\/organisation\/members\/?$/,
    label: 'page_titles.members',
    keep: ['q', 'status'],
    commands: () => ['/organisation/members'],
  },
  {
    pattern: /^\/organisation\/access\/?$/,
    label: 'page_titles.access_overview',
    keep: ['lens', 'q', 'access', 'coverage', 'attention'],
    commands: () => ['/organisation/access'],
  },
  {
    pattern: /^\/customers\/([^/]+)\/team-access\/?$/,
    label: 'customer_page.tab_team_access',
    // Its own origin (the overview, say) rides along, so back from the tab still
    // goes where the tab was opened from. It is re-checked at the next hop.
    keep: ['returnTo'],
    commands: id => ['/customers', id, 'team-access'],
  },
  {
    pattern: /^\/customers\/([^/]+)\/members\/?$/,
    label: 'customer_page.tab_members',
    keep: [],
    commands: id => ['/customers', id, 'members'],
  },
];

const FALLBACK: BreadcrumbItem = { label: 'page_titles.members', url: ['/organisation/members'] };

/** The target a `returnTo` names, as a back-link item, or null when it is not one of `targets`. */
function resolveOrigin(
  returnTo: string | null | undefined,
  targets: readonly Target[]
): BreadcrumbItem | null {
  if (!returnTo) return null;

  // Split on `?` only: an org id is `ORG#…`, so `#` is part of a path here, and a
  // fragment never travels in `returnTo`.
  const [rawPath, rawQuery = ''] = returnTo.split('?');
  let path: string;
  try {
    // Decoded once here; the router encodes the segments again exactly once.
    path = rawPath
      .split('/')
      .map(segment => decodeURIComponent(segment))
      .join('/');
  } catch {
    return null;
  }

  for (const target of targets) {
    const match = target.pattern.exec(path);
    if (!match) continue;
    const params = new URLSearchParams(rawQuery);
    const queryParams: Record<string, string> = {};
    for (const key of target.keep) {
      const value = params.get(key);
      if (value) queryParams[key] = value;
    }
    // A segment array, never a path string: `routerLink` encodes what it is
    // handed, so a pre-encoded id (`%23`) would be encoded twice.
    return { label: target.label, url: target.commands(match[1]), queryParams };
  }
  return null;
}

/** The single ancestor of the editor for a given `returnTo` (one item: a back link). */
export function memberAccessAncestors(returnTo: string | null | undefined): BreadcrumbItem[] {
  return [resolveOrigin(returnTo, TARGETS) ?? { ...FALLBACK, queryParams: {} }];
}

/**
 * The single ancestor of a customer's page. Customers is its home; but a customer
 * page opened from the Access overview goes back to the overview, with the lens
 * and filters it had, not to the Customers list.
 */
export function customerDetailAncestors(returnTo: string | null | undefined): BreadcrumbItem[] {
  const origin = resolveOrigin(
    returnTo,
    TARGETS.filter(t => t.label === 'page_titles.access_overview')
  );
  return [origin ?? { label: 'page_titles.customers', url: '/customers' }];
}
